// SPDX-License-Identifier: Apache-2.0

import Request from "./Request";
import Response from "./Response";
import Initialized from "./Initialized";
import ChannelProposal from "./ChannelProposal";
import ChannelCreated from "./ChannelCreated";
import UpdateChannel from "./UpdateChannel";
import ChannelClosed from "./ChannelClosed";
import ChannelInfo from "./ChannelInfo";
import SignETHData from "./SignETHData";
import SendETHTx from "./SendETHTx";
import Success from "./Success";
import Error from "./Error";
import PerunMessage from "./PerunMessage";
import { TypedJSON } from "typedjson";
import PerunObject from "./PerunObject";
import ProposalResponse from "./ProposalResponse";
import OpenChannel from "./OpenChannel";
import SignResponse from "./SignResponse";
import EthereumInitialize from "./EthereumInitialize";
import CloseChannel from "./CloseChannel";
import GetChannelInfo from "./GetChannelInfo";
import GetAssets from "./GetAssets";
import GetAssetsResponse from "./GetAssetsResponse";
import SendETHTxResponse from "./SendETHTxResponse";
import GetChains from "./GetChains";
import GetChainsResponse from "./GetChainsResponse";
import SetAdjTxSender from "./SetAdjTxSender";
import GetDecimals from "./GetDecimals";
import GetDecimalsResponse from "./GetDecimalsResponse";
import FundingError from "./FundingError";
import GetTimeout from "./GetTimeout";
import GetTimeoutResponse from "./GetTimeoutResponse";
import GetQuoteResponse from "./GetQuoteResponse";
import GetQuote from "./GetQuote";
import GetSignedState from "./GetSignedState";
import SignedState from "./SignedState";
import Dispose from "./Dispose";
import StellarInitialize from "./StellarInitialize";
import SignSteData from "./SignSteData";
import SendSteTx from "./SendSteTx";
import SendSteTxResponse from "./SendSteTxResponse";
import GetBalance from "./GetBalance";
import GetBalanceResponse from "./GetBalanceResponse";
import CrossContractInitialize from "./CrossContractInitialize";
import SendNervTx from "./SendNervTx";
import SignNervData from "./SignNervData";
import SendNervTxResponse from "./SendNervTxResponse";
import NervosInitialize from "./NervosInitialize";
import FinishSwap from "./FinishSwap";
import RecoverInitialize from "./RecoverInitialize";
import RecoverFunds from "./RecoverFunds";
import OpenChannelFound from "./OpenChannelFound";
import FundingChannelFound from "./FundingChannelFound";
import RecoverFunding from "./RecoverFunding";
import GetGuardrails from "./GetGuardrails";
import GetGuardrailsResponse from "./GetGuardrailsResponse";
import GetLPPositions from "./GetLPPositions";
import GetLPPositionsResponse from "./GetLPPositionsResponse";
import LPDepositInit from "./LPDepositInit";
import LPDepositInitResponse from "./LPDepositInitResponse";
import LPWithdrawInit from "./LPWithdrawInit";
import LPWithdrawInitResponse from "./LPWithdrawInitResponse";
import LPSubmitSigned from "./LPSubmitSigned";
import LPSubmitSignedResponse from "./LPSubmitSignedResponse";
import LPConfirmETH from "./LPConfirmETH";
import LPConfirmETHResponse from "./LPConfirmETHResponse";

// Runtime constant which is necessary because we want to compare with the
// `typeof Request` pendants and alike.
const _messageType = {
  Request: Request,
  Response: Response,
  EthereumInitialize: EthereumInitialize,
  StellarInitialize: StellarInitialize,
  NervosInitialize: NervosInitialize,
  CrossContractInitialize: CrossContractInitialize,
  Initialized: Initialized,
  Dispose: Dispose,
  GetAssets: GetAssets,
  GetAssetsResponse: GetAssetsResponse,
  GetChains: GetChains,
  GetChainsResponse: GetChainsResponse,
  GetDecimals: GetDecimals,
  GetDecimalsResponse: GetDecimalsResponse,
  GetTimeout: GetTimeout,
  GetTimeoutResponse: GetTimeoutResponse,
  GetQuote: GetQuote,
  GetQuoteResponse: GetQuoteResponse,
  GetBalance: GetBalance,
  GetBalanceResponse: GetBalanceResponse,
  ChannelProposal: ChannelProposal,
  ChannelCreated: ChannelCreated,
  ChannelClosed: ChannelClosed,
  ChannelInfo: ChannelInfo,
  UpdateChannel: UpdateChannel,
  OpenChannel: OpenChannel,
  CloseChannel: CloseChannel,
  GetChannelInfo: GetChannelInfo,
  SetAdjTxSender: SetAdjTxSender,
  SendETHTx: SendETHTx,
  SendSteTx: SendSteTx,
  SendNervTx: SendNervTx,
  SendETHTxResponse: SendETHTxResponse,
  SendSteTxResponse: SendSteTxResponse,
  SendNervTxResponse: SendNervTxResponse,
  SignETHData: SignETHData,
  SignSteData: SignSteData,
  SignNervData: SignNervData,
  SignResponse: SignResponse,
  ProposalResponse: ProposalResponse,
  Success: Success,
  FundingError: FundingError,
  Error: Error,
  GetSignedState: GetSignedState,
  SignedState: SignedState,
  SendSignedState: SignedState,
  FinishSwap: FinishSwap,
  RecoverInitialize: RecoverInitialize,
  RecoverFunds: RecoverFunds,
  OpenChannelFound: OpenChannelFound,
  FundingChannelFound: FundingChannelFound,
  RecoverFunding: RecoverFunding,
  GetGuardrails: GetGuardrails,
  GetGuardrailsResponse: GetGuardrailsResponse,
  GetLPPositions: GetLPPositions,
  GetLPPositionsResponse: GetLPPositionsResponse,
  LPDepositInit: LPDepositInit,
  LPDepositInitResponse: LPDepositInitResponse,
  LPWithdrawInit: LPWithdrawInit,
  LPWithdrawInitResponse: LPWithdrawInitResponse,
  LPSubmitSigned: LPSubmitSigned,
  LPSubmitSignedResponse: LPSubmitSignedResponse,
  LPConfirmETH: LPConfirmETH,
  LPConfirmETHResponse: LPConfirmETHResponse,
};

// Lift the type of _messageType into the Typesystem.
// Now the `keys` and corresponding types are accessible at compile time.
type messageType = typeof _messageType;

// MessageTypeName is a sum type consisting of all keys
// ("Request" | "OpenChannel" | "CloseChannel" | ... ) of `messageType`.
export type MessageTypeName = keyof messageType;

// MessageType is a sum type consisting of all the types for each key
// (typeof Request | typeof OpenChannel | typeof CloseChannel | ... ) of `messageType`.
export type MessageType = messageType[MessageTypeName];

// isMessageTypeName is a type predicate function telling the compiler whether or not
// the input `s` is of the concrete type `MessageTypeName`.
function isMessageTypeName(s: string): s is MessageTypeName {
  return Object.keys(_messageType).includes(s);
}

PerunObject.fromJSON = (js: any): PerunObject => {
  const raw = JSON.stringify(js.message);
  let message: PerunMessage;

  // Allows compiler to narrow the type of this variable subsequently.
  const type = js.type;
  if (isMessageTypeName(type)) {
    message = TypedJSON.parse(raw, _messageType[type])!;
  } else {
    throw new Error(`unknown type "${js.type}"`);
  }

  return new PerunObject(message);
};
